import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Button, Typography, TextField, Paper, RadioGroup,
  FormControlLabel, Radio, Checkbox, FormGroup
} from "@mui/material";
import { buildApiUrl } from "./config";

// Helper function to convert video URLs to embeddable format
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }
  
  // Vimeo
  if (url.includes('vimeo.com/')) {
    const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
    return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
  }
  
  // Already an embed URL
  if (url.includes('/embed/')) {
    return url;
  }
  
  return null;
};

const HomeworkViewer = ({ homework: homeworkProp, studentId: studentIdProp }) => {
  const { studentId: routeStudentId, homeworkId } = useParams();
  const studentId = studentIdProp || routeStudentId || localStorage.getItem("studentId");
 // ✅ Use prop or route parameter for studentId
  const [homework, setHomework] = useState(homeworkProp || null);
  const navigate = useNavigate();

  // Log useParams values and resolved studentId
  console.log("🔍 useParams:", routeStudentId, homeworkId);
  console.log("✅ Resolved studentId:", studentId);

  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  
  // ✅ New state for math examples
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [mathExample, setMathExample] = useState(null);
  const [loadingExample, setLoadingExample] = useState(false);
  const [studentForm, setStudentForm] = useState(null); // ✅ Add student form state
  
  // ✅ New state for hints
  const [showHintModal, setShowHintModal] = useState(false);
  const [hint, setHint] = useState(null);
  const [loadingHint, setLoadingHint] = useState(false);

  useEffect(() => {
    if (homeworkProp) return; // Use passed-in homework if available

    if (!homeworkId || homeworkId === "undefined") {
      setHomework(null);
      return;
    }

    // Fetch homework data
    fetch(`${buildApiUrl()}/get-homework/${homeworkId}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("📥 Homework data loaded:", data);
        console.log("📥 Questions count:", data?.questions?.length);
        if (data?.questions?.[0]) {
          console.log("📥 First question media:", {
            hasImage: !!data.questions[0].image,
            imageUrl: data.questions[0].image,
            hasVideo: !!data.questions[0].videoUrl,
            videoUrl: data.questions[0].videoUrl,
            optionImages: data.questions[0].optionImages
          });
        }
        setHomework(data);
      })
      .catch(() => setHomework(null));
  }, [homeworkId, homeworkProp]);

  // ✅ Fetch student form information
  useEffect(() => {
    async function fetchStudentForm() {
      if (!studentId) return;
      
      try {
        const response = await fetch(`${buildApiUrl()}/synced-students`);
        if (response.ok) {
          const data = await response.json();
          const student = data.students.find(s => s.school_id === studentId);
          if (student) {
            setStudentForm(student.form);
          }
        }
      } catch (error) {
        console.error("Failed to fetch student form:", error);
      }
    }
    fetchStudentForm();
  }, [studentId]);

  if (!homework || !Array.isArray(homework.questions) || homework.questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📝</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Assignment Available</h2>
          <p className="text-gray-600 mb-6">The homework assignment you're looking for is not available.</p>
          <button
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            onClick={() => navigate("/student-dashboard")}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const questions = homework.questions;
  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const handleAnswerChange = (name, value) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, option) => {
    const current = answers[name] || [];
    if (current.includes(option)) {
      setAnswers((prev) => ({ ...prev, [name]: current.filter((o) => o !== option) }));
    } else {
      setAnswers((prev) => ({ ...prev, [name]: [...current, option] }));
    }
  };

  const handleSubmit = () => {
    const unanswered = questions.filter(q => !answers[q.name] ||
      (Array.isArray(answers[q.name]) && answers[q.name].length === 0));
    if (unanswered.length > 0) {
      alert(`Please answer all questions. ${unanswered.length} question(s) remaining.`);
      return;
    }
    setShowSubmitModal(true);
  };

  const confirmSubmit = async () => {
    if (!studentId) {
      alert("Student ID not found. Please log in again.");
      return;
    }
    // Prepare answers for backend
    const normalize = (value) => String(value).trim().toLowerCase();
    const fullAnswers = questions.map((q) => {
      const selected = answers[q.name] || null;
      const correct = q.correct || null;
      let isCorrect = false;
      if (q.type === "multiple") {
        isCorrect = Array.isArray(selected) &&
          Array.isArray(correct) &&
          selected.length === correct.length &&
          selected.every((s) => correct.some((c) => normalize(s) === normalize(c)));
      } else {
        isCorrect = normalize(selected) === normalize(correct);
      }
      return {
        label: q.label,
        options: q.options || [],
        correct,
        selected,
        isCorrect,
      };
    });
    const score = fullAnswers.filter((a) => a.isCorrect).length;
    const total = fullAnswers.length;

    await fetch(`${buildApiUrl()}/submit-homework/${studentId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        answers: fullAnswers, // ✅ Include answers
        score,                // ✅ Include score
        total,                // ✅ Include total
        homework_id: homework.id, // ✅ Include homework_id
      }),
    });

    setShowSubmitModal(false);
    // Navigate to the homework result page
    navigate(`/homework-result/${studentId}/${homework.id}`); // ✅ Redirect to the result page
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAnsweredCount = () => {
    return questions.filter(q => answers[q.name] &&
      (!Array.isArray(answers[q.name]) || answers[q.name].length > 0)).length;
  };

  // ✅ Function to generate math examples
  const generateMathExample = async (question) => {
    setLoadingExample(true);
    setShowExampleModal(true);
    setMathExample(null);

    try {
      const response = await fetch(`${buildApiUrl()}/generate-math-example`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question.label,
          subject: homework.subject,
          type: question.type,
          student_form: studentForm // ✅ Include student form for age-appropriate examples
        }),
      });

      if (response.ok) {
        const example = await response.json();
        setMathExample(example);
      } else {
        setMathExample({
          example_question: "Sorry, couldn't generate an example right now.",
          answer: "Please try again later.",
          steps: ["Technical error occurred"],
          explanation: "Please contact your teacher for help."
        });
      }
    } catch (error) {
      console.error('Error generating math example:', error);
      setMathExample({
        example_question: "Sorry, couldn't generate an example right now.",
        answer: "Please try again later.",
        steps: ["Network error occurred"],
        explanation: "Please check your internet connection and try again."
      });
    } finally {
      setLoadingExample(false);
    }
  };

  // ✅ Function to generate hints for any subject
  const generateHint = async (question) => {
    setLoadingHint(true);
    setShowHintModal(true);
    setHint(null);

    try {
      const response = await fetch(`${buildApiUrl()}/generate-hint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question.label,
          subject: homework.subject,
          type: question.type,
          student_form: studentForm,
          options: question.options || []
        }),
      });

      if (response.ok) {
        const hintData = await response.json();
        setHint(hintData);
      } else {
        setHint({
          hint: "Sorry, couldn't generate a hint right now. Try thinking about what you've learned in class about this topic.",
          guidance: "Break the question down into smaller parts.",
          context: "Please try again later or ask your teacher for help."
        });
      }
    } catch (error) {
      console.error('Error generating hint:', error);
      setHint({
        hint: "Sorry, couldn't generate a hint right now. Try thinking about what you've learned in class about this topic.",
        guidance: "Break the question down into smaller parts.",
        context: "Please check your internet connection and try again."
      });
    } finally {
      setLoadingHint(false);
    }
  };

  // ✅ Check if current question is math-related
  const isMathQuestion = (question) => {
    // Check if homework is marked as math or if question is marked as math
    const homeworkIsMath = homework?.is_math || 
                          (homework?.subject && homework.subject.toLowerCase().includes('math'));
    const questionIsMath = question?.is_math;
    
    // Also check question content for math keywords
    const mathKeywords = ['solve', 'calculate', 'equation', 'formula', '+', '-', '×', '÷', '=', 'find x', 'algebra', 'geometry'];
    const questionHasMathKeywords = mathKeywords.some(keyword => 
      question.label.toLowerCase().includes(keyword.toLowerCase())
    );
    
    return homeworkIsMath || questionIsMath || questionHasMathKeywords;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{homework.title}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                <span className="flex items-center gap-1">
                  <span>📚</span>
                  {homework.subject}
                </span>
                <span className="flex items-center gap-1">
                  <span>📅</span>
                  Due: {formatDate(homework.dueDate)}
                </span>
                <span className="flex items-center gap-1">
                  <span>✅</span>
                  {getAnsweredCount()}/{questions.length} answered
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Progress</div>
              <div className="text-lg font-semibold text-blue-600">
                {currentIndex + 1} of {questions.length}
              </div>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Assignment Description */}
        {homework.description && currentIndex === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600">ℹ️</span>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
                <p className="text-blue-800">{homework.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Question Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">{currentIndex + 1}</span>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">{currentQuestion.label}</h2>
                <div className="text-sm text-gray-500 mt-1">
                  Question {currentIndex + 1} of {questions.length}
                </div>
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="space-y-4">
            {currentQuestion.type === "text" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Answer</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={answers[currentQuestion.name] || ""}
                  onChange={(e) => handleAnswerChange(currentQuestion.name, e.target.value)}
                  placeholder="Type your answer here..."
                />
              </div>
            )}

            {currentQuestion.type === "paragraph" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Answer</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={6}
                  value={answers[currentQuestion.name] || ""}
                  onChange={(e) => handleAnswerChange(currentQuestion.name, e.target.value)}
                  placeholder="Write your paragraph here..."
                />
              </div>
            )}

            {currentQuestion.type === "truefalse" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select your answer</label>
                <div className="space-y-3">
                  {["True", "False"].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        answers[currentQuestion.name] === option
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={currentQuestion.name}
                        value={option}
                        checked={answers[currentQuestion.name] === option}
                        onChange={() => handleAnswerChange(currentQuestion.name, option)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        answers[currentQuestion.name] === option
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}>
                        {answers[currentQuestion.name] === option && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <span className="font-medium text-gray-900">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {currentQuestion.type === "single" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select one answer</label>
                <div className="space-y-3">
                  {currentQuestion.options.map((option, idx) => {
                    // Debug log for option images
                    if (idx === 0 && currentQuestion.optionImages) {
                      console.log('🔍 Single choice - optionImages:', currentQuestion.optionImages);
                      console.log('🔍 Option 0 has image?', !!currentQuestion.optionImages[0]);
                      console.log('🔍 Option 0 image URL:', currentQuestion.optionImages[0]);
                    }
                    return (
                    <label
                      key={idx}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        answers[currentQuestion.name] === option
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={currentQuestion.name}
                        value={option}
                        checked={answers[currentQuestion.name] === option}
                        onChange={() => handleAnswerChange(currentQuestion.name, option)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        answers[currentQuestion.name] === option
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}>
                        {answers[currentQuestion.name] === option && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <span className="font-medium text-gray-900">{option}</span>
                        {currentQuestion.optionImages && currentQuestion.optionImages[idx] && (
                          <img 
                            src={currentQuestion.optionImages[idx]} 
                            alt={`Option ${idx + 1}`} 
                            className="h-12 w-12 object-cover rounded border border-gray-200" 
                            onError={(e) => {
                              console.error('❌ Failed to load option image:', currentQuestion.optionImages[idx]);
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => console.log('✅ Option image loaded:', currentQuestion.optionImages[idx])}
                          />
                        )}
                      </div>
                    </label>
                    );
                  })}
                </div>
              </div>
            )}

            {currentQuestion.type === "multiple" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select all that apply</label>
                <div className="space-y-3">
                  {currentQuestion.options.map((option, idx) => {
                    const isSelected = (answers[currentQuestion.name] || []).includes(option);
                    return (
                      <label
                        key={idx}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(currentQuestion.name, option)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                          isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 flex items-center gap-3">
                          <span className="font-medium text-gray-900">{option}</span>
                          {currentQuestion.optionImages && currentQuestion.optionImages[idx] && (
                            <img 
                              src={currentQuestion.optionImages[idx]} 
                              alt={`Option ${idx + 1}`} 
                              className="h-12 w-12 object-cover rounded border border-gray-200" 
                              onError={(e) => {
                                console.error('❌ Failed to load option image:', currentQuestion.optionImages[idx]);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => console.log('✅ Option image loaded:', currentQuestion.optionImages[idx])}
                            />
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Question Media - Video */}
            {currentQuestion.videoUrl && (
              <div className="mt-6 mb-6">
                {getEmbedUrl(currentQuestion.videoUrl) ? (
                  <iframe
                    src={getEmbedUrl(currentQuestion.videoUrl)}
                    className="w-full max-w-2xl h-64 rounded-lg border-2 border-purple-200 mx-auto"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Question video"
                  />
                ) : (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-800">📹 Video: <a href={currentQuestion.videoUrl} target="_blank" rel="noopener noreferrer" className="underline">{currentQuestion.videoUrl}</a></p>
                  </div>
                )}
              </div>
            )}

            {/* Question Media - Image */}
            {currentQuestion.image && (
              <div className="mt-6">
                <img
                  src={currentQuestion.image}
                  alt="Question visual"
                  className="max-w-full rounded-lg shadow-sm border border-gray-200"
                />
              </div>
            )}

            {/* ✅ Universal Hint Button for all questions */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => generateHint(currentQuestion)}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors duration-200 text-sm font-medium"
                >
                  <span className="text-lg">💡</span>
                  Get a hint
                </button>
                {isMathQuestion(currentQuestion) && (
                  <button
                    onClick={() => generateMathExample(currentQuestion)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors duration-200 text-sm font-medium"
                  >
                    <span className="text-lg">🧮</span>
                    Show example
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Get helpful guidance without revealing the answer
                {isMathQuestion(currentQuestion) && " • 🧮 See a similar problem with step-by-step solution"}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="flex items-center gap-2">
            {questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all duration-200 ${
                  idx === currentIndex
                    ? "bg-blue-600 text-white"
                    : answers[questions[idx].name]
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {currentIndex < questions.length - 1 ? (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              onClick={() => setCurrentIndex((i) => i + 1)}
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              onClick={handleSubmit}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Submit Assignment
            </button>
          )}
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Submit Assignment?</h3>
              <p className="text-gray-600">
                You have answered {getAnsweredCount()} out of {questions.length} questions.
                Once submitted, you cannot make changes.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                onClick={() => setShowSubmitModal(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                onClick={confirmSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Math Example Modal */}
      {showExampleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🧮</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Math Example</h3>
                    <p className="text-gray-600 text-sm">Similar problem with solution</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExampleModal(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingExample ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="text-gray-600">Generating example...</span>
                  </div>
                </div>
              ) : mathExample ? (
                <div className="space-y-6">
                  {/* Example Question */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📝 Example Problem</h4>
                    <p className="text-blue-800">{mathExample.example_question}</p>
                  </div>

                  {/* Answer */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">✅ Answer</h4>
                    <p className="text-green-800 font-mono text-lg">{mathExample.answer}</p>
                  </div>

                  {/* Steps */}
                  {mathExample.steps && mathExample.steps.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-900 mb-3">🚀 Step-by-Step Solution</h4>
                      <div className="space-y-3">
                        {mathExample.steps.map((step, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                              {index + 1}
                            </div>
                            <p className="text-purple-800">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Explanation */}
                  {mathExample.explanation && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-900 mb-2">💡 Concept Explanation</h4>
                      <p className="text-yellow-800">{mathExample.explanation}</p>
                    </div>
                  )}

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowExampleModal(false)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
                    >
                      Got it! Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-gray-500">No example available</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Hint Modal */}
      {showHintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">💡</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Helpful Hint</h3>
                    <p className="text-gray-600 text-sm">Guidance to help you solve this question</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHintModal(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingHint ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
                    <span className="text-gray-600">Generating hint...</span>
                  </div>
                </div>
              ) : hint ? (
                <div className="space-y-6">
                  {/* Main Hint */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">💡 Hint</h4>
                    <p className="text-yellow-800">{hint.hint}</p>
                  </div>

                  {/* Subject-specific additional information */}
                  {(hint.concept || hint.principle || hint.background || hint.location) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">
                        📚 {hint.concept ? 'Key Concept' : 
                             hint.principle ? 'Scientific Principle' : 
                             hint.background ? 'Background Info' : 
                             hint.location ? 'Location Info' : 'Additional Info'}
                      </h4>
                      <p className="text-blue-800">
                        {hint.concept || hint.principle || hint.background || hint.location}
                      </p>
                    </div>
                  )}

                  {/* Approach/Guidance */}
                  {(hint.approach || hint.guidance || hint.tip || hint.process) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2">
                        🎯 {hint.approach ? 'Approach' : 
                             hint.guidance ? 'Guidance' : 
                             hint.tip ? 'Tip' : 
                             hint.process ? 'Process' : 'How to Solve'}
                      </h4>
                      <p className="text-green-800">
                        {hint.approach || hint.guidance || hint.tip || hint.process}
                      </p>
                    </div>
                  )}

                  {/* Context */}
                  {(hint.context || hint.feature) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-900 mb-2">
                        🌍 {hint.context ? 'Context' : 'Features'}
                      </h4>
                      <p className="text-purple-800">
                        {hint.context || hint.feature}
                      </p>
                    </div>
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-600 text-sm italic">
                      💭 Remember: This hint is designed to guide your thinking without giving away the answer. 
                      Use this information to help you reason through the problem yourself!
                    </p>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowHintModal(false)}
                      className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors duration-200"
                    >
                      Thanks! Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="text-gray-500">No hint available</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeworkViewer;
